/**
 * Pure onboarding wizard state and copy (no React, no IO).
 * Used by SignupWizard and covered by Vitest for safe refactors of step flow.
 */

import { UserRole } from '@repo/shared-types';

export type ManagementApproach = 'self-manage-all' | 'pm-for-all' | 'mixed';

/** PM subset when `isSelfManagingOwner` — same order as full PM flow subset. */
export const PM_STEP_IDS_SMO = ['welcome', 'personal', 'property', 'review'] as const;

export type SignupStepDef = { id: string; label: string };

export const SIGNUP_STEP_BASE: Record<UserRole, readonly SignupStepDef[]> = {
  [UserRole.TENANT]: [
    { id: 'lease', label: 'Lease Signing' },
    { id: 'personal', label: 'Personal Info' },
    { id: 'emergency', label: 'Emergency Contact' },
    { id: 'portal', label: 'Portal Access' },
  ],
  [UserRole.VENDOR]: [
    { id: 'business', label: 'Business Info' },
    { id: 'licenses', label: 'Licenses & Ins' },
    { id: 'w9', label: 'Tax / W9' },
    { id: 'services', label: 'Services' },
    { id: 'bank', label: 'Bank Account' },
    { id: 'approval', label: 'Approval' },
  ],
  [UserRole.PROPERTY_MANAGER]: [
    { id: 'welcome', label: 'Welcome' },
    { id: 'personal', label: 'Personal Information' },
    { id: 'company', label: 'Company Profile' },
    { id: 'license', label: 'License & Accreditation' },
    { id: 'property', label: 'Add Properties' },
    { id: 'review', label: 'Review & Create Account' },
  ],
  [UserRole.OWNER]: [
    { id: 'owner-profile', label: 'Owner Profile' },
    { id: 'property-management-approach', label: 'Management Approach' },
    { id: 'bank', label: 'Bank Setup' },
    { id: 'tax', label: 'Tax Documents' },
    { id: 'workflow-preferences', label: 'Workflow Preferences' },
    { id: 'add-owner-properties', label: 'Add Your Properties' },
    { id: 'review-portal', label: 'Review & Portal Access' },
  ],
  [UserRole.INVESTOR]: [
    { id: 'terms', label: 'Inv. Terms' },
    { id: 'entity', label: 'Entity Info' },
    { id: 'accreditation', label: 'Accreditation' },
    { id: 'bank', label: 'Bank Account' },
    { id: 'legal', label: 'Legal Sign-off' },
  ],
  [UserRole.ADMIN]: [],
  [UserRole.SYSTEM_ADMIN]: [],
  [UserRole.PLATFORM_ADMIN]: [],
};

export const PM_STEP_TITLE_DESCRIPTION: Record<string, { title: string; description: string }> = {
  personal: { title: 'Personal Information', description: 'Tell us about yourself.' },
  company: { title: 'Company Profile', description: 'Tell us about your business' },
  license: { title: 'License & Accreditation', description: 'Verify your professional credentials' },
  property: { title: 'Add Properties', description: 'Add properties to manage' },
  'migration-upload': { title: 'Upload Portfolio', description: 'Upload a file to migrate your portfolio.' },
  'migration-review': { title: 'Review Migration', description: 'Review the parsed data and any warnings before continuing.' },
  review: { title: 'Review & Set Password', description: 'Review your information and create your account' },
};

export const OWNER_STEP_TITLE_DESCRIPTION: Record<string, { title: string; description: string }> = {
  'owner-profile': { title: 'Owner Profile', description: 'Tell us about yourself and how to reach you.' },
  'property-management-approach': { title: 'Management Approach', description: 'Choose how your properties will be managed.' },
  bank: { title: 'Bank Setup for Distributions', description: 'Where to receive rental income and distributions.' },
  tax: { title: 'Tax Documents', description: 'Tax ID and W-9 for 1099 reporting.' },
  'workflow-preferences': { title: 'Workflow Preferences', description: 'Notifications and how you prefer to work with your PM.' },
  'add-owner-properties': { title: 'Add Your Properties', description: 'Add properties to your portfolio.' },
  'review-portal': { title: 'Review & Portal Access', description: 'Review your information and create your account.' },
};

export const INVESTOR_STEP_TITLE_DESCRIPTION: Record<string, { title: string; description: string }> = {
  terms: { title: 'Investment Terms', description: 'Review and confirm investment details.' },
  entity: { title: 'Entity Information', description: 'Identify investing entity and contact information.' },
  accreditation: { title: 'Accreditation Proof', description: 'Verify accredited investor status (SEC requirement).' },
  bank: { title: 'Bank Routing for Distributions', description: 'Configure bank account for receiving investment distributions.' },
  legal: { title: 'Legal Sign-off', description: 'Review legal documents and execute investment agreement.' },
};

export const VENDOR_STEP_TITLE_DESCRIPTION: Record<string, { title: string; description: string }> = {
  business: { title: 'Business Info', description: 'Tell us about your company and primary contact.' },
  licenses: { title: 'Licenses & Insurance', description: 'Add your trade licenses and insurance details.' },
  w9: { title: 'Tax / W9', description: 'Provide tax information for payments.' },
  services: { title: 'Services', description: 'Select the services you offer and your service area.' },
  bank: { title: 'Bank Account', description: 'Where to receive payments for completed work.' },
  approval: { title: 'Approval', description: 'Review and submit for marketplace approval.' },
};

export function getWelcomeBlurb(role: UserRole, isSelfManagingOwner: boolean): string {
  if (role === UserRole.OWNER) {
    return "We'll collect your property details, contact and bank information, and set up your portal access. After that you'll create your account and sign in. Use the button below to continue.";
  }
  if (isSelfManagingOwner) {
    return "We'll collect your contact details and properties. After that you'll set a password and sign in. You can add more properties later from the Property Directory. Use the button below to continue.";
  }
  return "We'll collect your contact and company details, license information, and how you'd like to get started. After that you'll set a password and sign in. You can add properties right after signup or later from the Property Directory. Use the button below to continue.";
}

export function getSignupWelcomeRoleTitle(role: UserRole, isSelfManagingOwner: boolean): string {
  if (role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner) return 'Property Manager';
  if (role === UserRole.OWNER) return 'Property Owner';
  return 'Self Managing Owner';
}

export function getOnboardingHeaderTitle(readOnly: boolean, isSelfManagingOwner: boolean, role: UserRole): string {
  if (readOnly) return 'Onboarding Status';
  if (isSelfManagingOwner) return 'Onboard New Self Managing Owner';
  return `Onboard New ${role.replace('_', ' ')}`;
}

export function getOnboardingStepShortLabel(stepId: string, fallbackLabel: string): string {
  switch (stepId) {
    case 'welcome':
      return 'Role';
    case 'create-account':
      return 'Account';
    case 'personal':
    case 'entity':
      return 'Personal';
    case 'company':
      return 'Company';
    case 'license':
    case 'licenses':
      return 'License';
    case 'trust-accounting':
      return 'Trust';
    case 'property':
      return 'Properties';
    case 'migration-upload':
      return 'Upload';
    case 'migration-review':
      return 'Migration';
    case 'review':
    case 'review-portal':
      return 'Review';
    case 'owner-profile':
      return 'Profile';
    case 'property-management-approach':
      return 'Management';
    case 'bank':
      return 'Bank';
    case 'tax':
      return 'Tax';
    case 'workflow-preferences':
      return 'Preferences';
    case 'add-owner-properties':
      return 'Properties';
    case 'business':
      return 'Business';
    case 'w9':
      return 'W9';
    case 'services':
      return 'Services';
    case 'approval':
      return 'Approval';
    case 'terms':
      return 'Terms';
    case 'accreditation':
      return 'Accreditation';
    case 'legal':
      return 'Legal';
    default:
      return fallbackLabel;
  }
}

export function getOnboardingPrimaryFooterLabel(
  isLastStep: boolean,
  role: UserRole,
  isSelfManagingOwner: boolean | undefined,
  embedded: boolean | undefined,
  hasClerkSignupRequest: boolean
): string {
  if (!isLastStep) {
    if (role === UserRole.PROPERTY_MANAGER && !isSelfManagingOwner) return 'Next';
    return 'Next Step';
  }
  if (role === UserRole.VENDOR || role === UserRole.INVESTOR) return 'Submit for Approval';
  if (embedded && hasClerkSignupRequest) return 'Create your account';
  return 'Complete Onboarding';
}

/** Clamp step index to valid range for the current step list length. */
export function clampSignupStepIndex(stepIndex: number, stepsLength: number): number {
  if (stepsLength <= 0) return 0;
  return Math.max(0, Math.min(stepIndex, stepsLength - 1));
}

/** Advance by delta (+1 next, -1 back); returns clamped index. */
export function transitionSignupStepIndex(currentIndex: number, delta: number, stepsLength: number): number {
  return clampSignupStepIndex(currentIndex + delta, stepsLength);
}
